import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import { formatDate } from '@/lib/utils/custom-date/custom-date';
import { MbmProject } from '../../types/mbm-project.types';
import { parseMbmProjectHistory } from '../../utils/mbm-project-payload';
import { MbmProjectStatusBadge } from '../mbm-project-table-columns/mbm-project-table-columns';

export interface MbmProjectHistoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  project: MbmProject | null;
}

/** Read-only audit trail: every snapshot stored in the project's `MbmProjectHistory`, newest first. */
export const MbmProjectHistoryDialog = ({
  open,
  onOpenChange,
  project,
}: MbmProjectHistoryDialogProps) => {
  const { t } = useTranslation();

  const entries = useMemo(
    () =>
      parseMbmProjectHistory(project?.MbmProjectHistory).sort(
        (a, b) => new Date(b.CreateDate).getTime() - new Date(a.CreateDate).getTime()
      ),
    [project]
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{t('MBM_PROJECT_HISTORY')}</DialogTitle>
          <DialogDescription>
            {project ? `${project.ProjectName} · ${project.CustomerName}` : ''}
          </DialogDescription>
        </DialogHeader>

        {entries.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            {t('MBM_PROJECT_HISTORY_EMPTY')}
          </p>
        ) : (
          <ol className="flex max-h-96 flex-col gap-3 overflow-y-auto pr-1">
            {entries.map((entry) => (
              <li
                key={`${entry.CreateDate}-${entry.Status}-${entry.ProjectName}`}
                className="flex flex-col gap-1 rounded-md border p-3"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs text-muted-foreground">
                    {formatDate(new Date(entry.CreateDate), true)}
                  </span>
                  <MbmProjectStatusBadge status={entry.Status} t={t} />
                </div>
                <span className="text-sm font-medium">{entry.ProjectName}</span>
                <span className="text-xs text-muted-foreground">
                  {entry.CustomerName} · {entry.CustomerId} · {entry.ProjectId}
                </span>
              </li>
            ))}
          </ol>
        )}
      </DialogContent>
    </Dialog>
  );
};
